// Multi-stage SAP-pull upload modal. SPEC §4.1, §5 modals.
//
//   pick    → file picker (or duplicate-hash short-circuit)
//   analyze → spinner + "parsing N rows..." waiting on SSE
//   diff    → 3-cell stat block (rows · +new · +auto-matched)
//   applied → success state with Done

import React, { useState, useRef } from 'react';
import { TOKENS, UI_FONT, MONO_FONT, NUM_STYLE } from './tokens';
import { Modal, Mono, Icon, styles } from './primitives';
import { uploadSapPull } from './api';

const STAGES = { pick: 'pick', analyze: 'analyze', diff: 'diff', applied: 'applied' };

export default function UploadModal({ period, onClose, onApplied }) {
  const [stage, setStage] = useState(STAGES.pick);
  const [diff, setDiff] = useState(null);
  const [filename, setFilename] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  }

  async function handleFile(file) {
    if (!file) return;
    setFilename(file.name);
    setStage(STAGES.analyze);
    setError(null);
    try {
      const res = await uploadSapPull(file, period);
      const data = res.data;
      const imp = data.import || data;
      setDiff({
        rows: imp.record_count ?? 0,
        added: imp.rows_added ?? 0,
        unchanged: imp.rows_unchanged ?? 0,
        duplicate: data.duplicate || false,
        // Period the backend ended up assigning (derived from the file's
        // most-common posting-date quarter). Surface it so the parent
        // page can switch to it on apply — otherwise the user uploads,
        // clicks Apply, and lands on an empty workspace because the page
        // is still on the default (current) quarter.
        importQuarter: imp.quarter || null,
      });
      setStage(STAGES.diff);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
      setStage(STAGES.pick);
    }
  }

  function apply() {
    setStage(STAGES.applied);
    if (onApplied) onApplied({ importQuarter: diff?.importQuarter || null });
    // Auto-close after the user has a moment to register the result.
    setTimeout(onClose, 600);
  }

  return (
    <Modal title="Upload SAP pull" subtitle="Idempotent · re-uploads are safe" onClose={onClose}>
      {stage === STAGES.pick && (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              padding: '32px 18px',
              borderRadius: 10,
              border: `1.5px dashed ${dragOver ? TOKENS.accent : TOKENS.borderStrong}`,
              background: dragOver ? TOKENS.accentSoft : TOKENS.panelAlt,
              cursor: 'pointer',
              transition: 'border-color 120ms, background 120ms',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: TOKENS.accentSoft,
                color: TOKENS.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon d="M12 16V4m0 0l-5 5m5-5l5 5M4 20h16" size={18} sw={2} />
            </div>
            <div style={{ fontFamily: UI_FONT, fontSize: 13, fontWeight: 550 }}>
              Drop a SAP export here
            </div>
            <div
              style={{
                fontFamily: UI_FONT,
                fontSize: 11.5,
                color: TOKENS.ink3,
                lineHeight: 1.5,
                maxWidth: 380,
              }}
            >
              .xlsx · BSEG/BKPF · Auto-detects the period from the file.
              Rows are keyed by SAP doc number — re-uploading the same export is safe.
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
            <button
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              style={{ ...styles.primaryBtn, marginTop: 4 }}
            >
              Browse file
            </button>
          </div>
          {error && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 7,
                background: 'oklch(0.96 0.04 25)',
                border: `1px solid ${TOKENS.danger}`,
                color: TOKENS.danger,
                fontSize: 11.5,
                fontFamily: UI_FONT,
              }}
            >
              {error}
            </div>
          )}
        </>
      )}

      {stage === STAGES.analyze && (
        <div style={{ padding: '40px 18px', textAlign: 'center' }}>
          <div style={{ fontFamily: UI_FONT, fontSize: 14, fontWeight: 600 }}>
            Parsing {filename || 'file'}…
          </div>
          <div
            style={{
              marginTop: 12,
              width: '100%',
              height: 4,
              background: TOKENS.panelAlt,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: '50%',
                height: '100%',
                background: TOKENS.accent,
                animation: 'recon-shimmer 1.2s linear infinite',
              }}
            />
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: TOKENS.ink3 }}>
            Running auto-match against unmatched invoices…
          </div>
          <style>{`@keyframes recon-shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }`}</style>
        </div>
      )}

      {stage === STAGES.diff && diff && (
        <div>
          {diff.duplicate && (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 7,
                background: 'oklch(0.96 0.04 70)',
                border: `1px solid ${TOKENS.warn}`,
                color: TOKENS.warn,
                fontFamily: UI_FONT,
                fontSize: 11.5,
              }}
            >
              <b>Already imported.</b> This file was uploaded previously — no changes were made.
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10,
              padding: '4px 0 14px',
            }}
          >
            <DiffStat label="Rows in file" value={diff.rows.toLocaleString()} sub={`${diff.unchanged.toLocaleString()} unchanged`} />
            <DiffStat
              label="New rows"
              value={`+${diff.added}`}
              sub={diff.added > 0 ? 'will be added' : 'no new rows'}
              color={diff.added > 0 ? TOKENS.ok : TOKENS.ink3}
            />
            <DiffStat label="Auto-matched" value="—" sub="awaiting run" />
          </div>
          {diff.added > 0 && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 7,
                background: 'oklch(0.96 0.04 150)',
                border: '1px solid oklch(0.78 0.10 150)',
                fontSize: 11.5,
                color: TOKENS.ink,
                lineHeight: 1.5,
              }}
            >
              Auto-match runs in the background. Anything that doesn't reconcile cleanly will appear in the unmatched SAP column.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={onClose} style={styles.ghostBtn}>
              Cancel
            </button>
            <button onClick={apply} style={{ marginLeft: 'auto', ...styles.primaryBtnLg }}>
              <Icon d="M5 12l4 4 10-10" size={13} sw={2.5} />
              Apply upload
            </button>
          </div>
        </div>
      )}

      {stage === STAGES.applied && (
        <div style={{ padding: '40px 18px', textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'oklch(0.96 0.04 150)',
              color: TOKENS.ok,
              margin: '0 auto 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon d="M5 12l4 4 10-10" size={26} sw={2.5} />
          </div>
          <Mono size={11} color={TOKENS.ink3}>
            UPLOAD APPLIED
          </Mono>
        </div>
      )}
    </Modal>
  );
}

function DiffStat({ label, value, sub, color = TOKENS.ink }) {
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 7,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
      }}
    >
      <div
        style={{
          fontFamily: MONO_FONT,
          fontSize: 9.5,
          color: TOKENS.ink3,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          ...NUM_STYLE,
          fontSize: 22,
          fontWeight: 500,
          color,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 10.5, color: TOKENS.ink3 }}>{sub}</div>
    </div>
  );
}
